
import { get, onValue, ref, push, update } from "firebase/database";
import { db } from "../../../functions/api_firebase";
import { obtenerNits } from "../../../functions/variables_globales";
import Swal from "sweetalert2";
import {
  esVacio,
  escaparHTML,
  formatearFecha,
  fechaAhora,
  normalizarNodo,
  ordenarPorCreacion,
} from "./datos_sos";

export {
  ESTADOS_SOS,
  normalizarEstado,
  esPendiente,
  normalizarNodo,
  ordenarPorCreacion,
} from "./datos_sos";


export const nitsVisibles = async () => {
  if (localStorage.getItem("Permisos") === "SUPER" || localStorage.getItem("CodigoCliente") === "900362633") {
    return await obtenerNits();
  }
  const nit = localStorage.getItem("CodigoCliente");
  return nit ? [nit] : [];
};


export const cierreMasivoSOS = async (lista, refrescarDatos) => {
  const { value: dias, isConfirmed } = await Swal.fire({
    title: "Cierre automático de SOS",
    html: `
      <p style="font-size:14px;color:#374151;margin-bottom:12px;">
        Se cerrarán los SOS <strong>Pendientes</strong> que lleven más de los días indicados sin atención,
        dentro de las <strong>${lista.length}</strong> alertas que tienes en pantalla.
      </p>
      <input
        id="dias-cierre"
        type="number"
        min="1"
        value="30"
        style="width:100%;padding:10px;border:1px solid #d1d5db;border-radius:6px;font-size:14px;box-sizing:border-box;"
      >
      <p style="font-size:12px;color:#9ca3af;margin-top:8px;">Días sin atender</p>
    `,
    showCancelButton: true,
    confirmButtonText: "Cerrar SOS",
    cancelButtonText: "Cancelar",
    confirmButtonColor: "#c0392b",
    width: 460,
    preConfirm: () => {
      const v = parseInt(document.getElementById("dias-cierre").value);
      if (!v || v < 1) {
        Swal.showValidationMessage("Ingresa un número de días válido.");
        return false;
      }
      return v;
    }
  });

  if (!isConfirmed) return;

  const ahora = Date.now();
  const operador = localStorage.getItem("NombreCompleto") || "Sistema";
  const fechaCierre = fechaAhora();

  const pendientes = lista.filter(item => {
    if (item.estado_final === "Cerrado") return false;
    if (esVacio(item.nit)) return false;
    if (!item._creacion_ms) return false;
    const diffDias = (ahora - item._creacion_ms) / (1000 * 60 * 60 * 24);
    return diffDias >= dias;
  });

  if (!pendientes.length) {
    await Swal.fire({
      title: "Sin resultados",
      text: `No hay SOS pendientes con más de ${dias} días sin atender.`,
      icon: "info",
      confirmButtonText: "Entendido"
    });
    return;
  }

  const { isConfirmed: confirmar } = await Swal.fire({
    title: `¿Cerrar ${pendientes.length} SOS?`,
    text: `Se cerrarán ${pendientes.length} alertas con más de ${dias} días sin atender.`,
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Sí, cerrar todos",
    cancelButtonText: "Cancelar",
    confirmButtonColor: "#c0392b",
  });

  if (!confirmar) return;

  try {
    const comentario = {
      texto: `Cierre automático — sin atención por más de ${dias} días.`,
      operador,
      fecha: fechaCierre,
    };

    const cambios = {};
    pendientes.forEach(item => {
      const base = `/sos/${String(item.nit)}/${String(item.key || item.id)}`;
      const idComentario = push(ref(db, `${base}/comentarios`)).key;
      cambios[`${base}/comentarios/${idComentario}`] = comentario;
      cambios[`${base}/estado`]                      = "Cerrado";
      cambios[`${base}/fecha_y_hora_soporte`]        = fechaCierre;
      cambios[`${base}/operador`]                    = operador;
    });

    await update(ref(db), cambios);

    await Swal.fire({
      title: "Listo",
      text: `Se cerraron ${pendientes.length} SOS correctamente.`,
      icon: "success",
      timer: 2500,
      showConfirmButton: false
    });

    await refrescarDatos?.();

  } catch (error) {
    console.error("Error en cierre masivo:", error);
    Swal.fire({ title: "Error", text: "No se pudo completar el cierre masivo.", icon: "error", confirmButtonText: "Entendido" });
  }
};


export const obtenerSOS = async () => {
  try {
    const nits = await nitsVisibles();
    const grupos = await Promise.all(
      nits.map(nit => get(ref(db, `/sos/${nit}`)).then(s => normalizarNodo(s.val(), nit)))
    );
    return ordenarPorCreacion(grupos.flat());
  } catch (error) {
    console.error("Error en obtenerSOS:", error);
    return [];
  }
};


export const suscribirSOS = (alRecibir, alFallar) => {
  let activo = true;
  let listo = false;
  let faltantes = 0;
  let temporizador = null;
  let arranque = null;

  const porNit = new Map();
  const cancelaciones = [];

  const emitir = () => {
    if (!activo || !listo) return;
    clearTimeout(temporizador);
    temporizador = setTimeout(() => {
      if (!activo) return;
      alRecibir(ordenarPorCreacion([].concat(...porNit.values())));
    }, 80);
  };

  const marcarRespuesta = () => {
    if (listo) return emitir();
    faltantes -= 1;
    if (faltantes <= 0) {
      listo = true;
      emitir();
    }
  };

  nitsVisibles()
    .then((nits) => {
      if (!activo) return;

      if (!nits.length) {
        listo = true;
        alRecibir([]);
        return;
      }

      faltantes = nits.length;

      arranque = setTimeout(() => {
        if (!activo || listo) return;
        listo = true;
        emitir();
      }, 8000);

      nits.forEach((nit) => {
        const cancelar = onValue(
          ref(db, `/sos/${nit}`),
          (snap) => {
            if (!activo) return;
            porNit.set(nit, normalizarNodo(snap.val(), nit));
            marcarRespuesta();
          },
          (error) => {
            console.error(`Error escuchando /sos/${nit}:`, error);
            if (!activo) return;
            porNit.set(nit, []);
            marcarRespuesta();
          }
        );
        cancelaciones.push(cancelar);
      });
    })
    .catch((error) => {
      console.error("Error en suscribirSOS:", error);
      if (activo) alFallar?.(error);
    });

  return () => {
    activo = false;
    clearTimeout(temporizador);
    clearTimeout(arranque);
    cancelaciones.forEach((cancelar) => cancelar());
  };
};


export const gestionarSOS = async (fila, refrescarDatos) => {
  const nit      = String(fila.nit || "");
  const recordId = String(fila.key || fila.id || "");
  const operador = localStorage.getItem("NombreCompleto") || "Operador";

  if (!nit || !recordId) {
    await Swal.fire({
      title: "Registro incompleto",
      text: "Esta alerta no tiene NIT o identificador y no se puede gestionar.",
      icon: "error",
      confirmButtonText: "Entendido"
    });
    return;
  }

  const base = `/sos/${nit}/${recordId}`;

  const [snapComentarios, snapSOS] = await Promise.all([
    get(ref(db, `${base}/comentarios`)),
    get(ref(db, base))
  ]);
  const dataSOS = snapSOS.val() || {};
  const comentariosNuevos = snapComentarios.val()
    ? Object.values(snapComentarios.val())
    : [];

  const observacionLegacy = dataSOS.observacion && dataSOS.observacion !== "N/A"
    ? [{
        texto:    dataSOS.observacion,
        operador: dataSOS.operador || "Operador",
        fecha:    formatearFecha(dataSOS.fecha_y_hora_soporte || dataSOS.fecha_y_hora_creacion) || "",
        legacy:   true
      }]
    : [];

  const comentariosExistentes = [...observacionLegacy, ...comentariosNuevos];

  const historialHTML = comentariosExistentes.length
    ? `<div style="text-align:left;max-height:180px;overflow-y:auto;margin-bottom:12px;border:1px solid #e5e7eb;border-radius:8px;padding:10px;">
        ${comentariosExistentes.map(c => `
          <div style="margin-bottom:10px;padding-bottom:10px;border-bottom:1px solid #f3f4f6;">
            <span style="font-size:11px;color:#9ca3af;">${escaparHTML(c.fecha)} — ${escaparHTML(c.operador)}</span>
            <p style="margin:4px 0 0;font-size:13px;color:#374151;white-space:pre-wrap;">${escaparHTML(c.texto)}</p>
          </div>`).join("")}
      </div>`
    : `<p style="color:#9ca3af;font-size:13px;margin-bottom:12px;">Sin comentarios previos.</p>`;

  const esCerrado = fila.estado_final === "Cerrado";

  const { isConfirmed, value } = await Swal.fire({
    title: "Gestionar SOS",
    html: `
      <p style="text-align:left;font-size:12px;font-weight:600;color:#6b7280;margin-bottom:6px;">HISTORIAL DE COMENTARIOS</p>
      ${historialHTML}
      <textarea
        id="nuevo-comentario"
        placeholder="Escribe un nuevo comentario..."
        style="width:100%;min-height:90px;padding:10px;border:1px solid #d1d5db;border-radius:6px;font-size:13px;resize:vertical;box-sizing:border-box;"
      ></textarea>
      ${!esCerrado ? `
        <label style="display:flex;align-items:center;gap:8px;margin-top:10px;font-size:13px;color:#374151;cursor:pointer;">
          <input type="checkbox" id="cerrar-sos" style="width:16px;height:16px;">
          Marcar como cerrado al guardar
        </label>` : ""}
    `,
    showCancelButton: true,
    confirmButtonText: "Guardar comentario",
    cancelButtonText: "Cancelar",
    width: 520,
    preConfirm: () => {
      const texto = document.getElementById("nuevo-comentario").value.trim();
      if (!texto) {
        Swal.showValidationMessage("Escribe un comentario antes de guardar.");
        return false;
      }
      const cerrar = !esCerrado && document.getElementById("cerrar-sos")?.checked;
      return { texto, cerrar };
    }
  });

  if (!isConfirmed || !value) return;

  try {
    const ahora = fechaAhora();
    const comentario = {
      texto:    value.texto,
      operador,
      fecha:    ahora,
    };

    const idComentario = push(ref(db, `${base}/comentarios`)).key;
    const cambios = {
      [`${base}/comentarios/${idComentario}`]: comentario,
      [`${base}/operador`]:             operador,
      [`${base}/fecha_y_hora_soporte`]: ahora,
    };
    if (value.cerrar) cambios[`${base}/estado`] = "Cerrado";

    await update(ref(db), cambios);

    await Swal.fire({ title: "Guardado", text: "Comentario agregado correctamente.", icon: "success", timer: 1800, showConfirmButton: false });
    await refrescarDatos?.();

  } catch (error) {
    console.error("Error al gestionar SOS:", error);
    Swal.fire({ title: "Error", text: "No se pudo guardar el comentario.", icon: "error", confirmButtonText: "Entendido" });
  }
};
